import { format, subDays, startOfWeek, startOfMonth, startOfYear } from 'date-fns';

/**
 * Date filter presets configuration
 */
export const DATE_PRESETS = {
  today: 'Today',
  thisWeek: 'This Week',
  thisMonth: 'This Month',
  last30: 'Last 30 Days',
  last90: 'Last 90 Days',
  ytd: 'Year to Date',
  custom: 'Custom Range'
};

/**
 * Date basis options
 */
export const DATE_BASIS = {
  due: 'Due Date',
  invoice: 'Invoice Date'
};

/**
 * Calculate date range based on preset
 */
export function getDateRangeForPreset(preset) {
  const today = new Date();

  switch(preset) {
    case 'today':
      return {
        startDate: format(today, 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      };
    case 'thisWeek':
      return {
        startDate: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      };
    case 'thisMonth':
      return {
        startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      };
    case 'last30':
      return {
        startDate: format(subDays(today, 30), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      };
    case 'last90':
      return {
        startDate: format(subDays(today, 90), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      };
    case 'ytd':
      return {
        startDate: format(startOfYear(today), 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd')
      };
    case 'custom':
    default:
      return {
        startDate: '',
        endDate: ''
      };
  }
}

/**
 * Parse date filter from URL search params
 */
export function parseDateFilterFromURL(searchParams) {
  const preset = searchParams.get('preset') || 'custom';
  const basis = searchParams.get('basis') || 'due';
  const start = searchParams.get('start') || '';
  const end = searchParams.get('end') || '';

  // If preset is not custom, calculate dates from preset
  if (preset !== 'custom') {
    const { startDate, endDate } = getDateRangeForPreset(preset);
    return {
      preset,
      basis,
      startDate,
      endDate
    };
  }

  // For custom preset, use URL params (empty by default)
  return {
    preset: 'custom',
    basis,
    startDate: start,
    endDate: end
  };
}

/**
 * Serialize date filter to URL search params
 */
export function serializeDateFilterToURL(filter) {
  const params = new URLSearchParams();

  params.set('preset', filter.preset);
  params.set('basis', filter.basis);

  if (filter.preset === 'custom') {
    if (filter.startDate) params.set('start', filter.startDate);
    if (filter.endDate) params.set('end', filter.endDate);
  }

  return params.toString();
}

/**
 * Build API query params from date filter
 */
export function buildAPIParams(filter) {
  const params = new URLSearchParams();

  if (filter.startDate) params.append('start_date', filter.startDate);
  if (filter.endDate) params.append('end_date', filter.endDate);
  params.append('date_basis', filter.basis);

  return params.toString();
}

/**
 * Validate date range
 */
export function validateDateRange(startDate, endDate) {
  if (!startDate || !endDate) return true;

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return false;
  }

  if (start > end) {
    return false;
  }

  return true;
}
